import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Navbar from './Navbar';
import SeoHead from './SeoHead';
import SiteFooter from './SiteFooter';
import TrustBar from './TrustBar';
import {
  BRAND_LANDING_PAGES,
  GENERIC_LANDING_PAGES,
  SITE_URL,
} from '../data/seoLandingPages';
import { getBrandLogo } from '../utils/brandLogos';

const StepsSection = ({ steps, title = 'Zo werkt het' }) => (
  <section className="px-4 py-14 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl rounded-[2rem] bg-white p-8 shadow-sm ring-1 ring-gray-100 md:p-12">
      <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
      <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {steps.map((step, index) => (
          <div key={step.title} className="rounded-[1.5rem] bg-gradient-to-br from-orange-50 to-white p-6 ring-1 ring-orange-100">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-900 text-lg font-bold text-white">
              {index + 1}
            </div>
            <h3 className="mt-5 text-xl font-semibold text-gray-900">{step.title}</h3>
            <p className="mt-3 text-sm leading-7 text-gray-600">{step.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FaqSection = ({ faq }) => (
  <section className="px-4 py-14 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl rounded-[2rem] bg-white p-8 shadow-sm ring-1 ring-gray-100 md:p-12">
      <h2 className="text-3xl font-bold text-gray-900">FAQ</h2>
      <div className="mt-8 grid gap-5 lg:grid-cols-2">
        {faq.map((item) => (
          <div key={item.question} className="rounded-[1.5rem] border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900">{item.question}</h3>
            <p className="mt-3 text-sm leading-7 text-gray-600">{item.answer}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const BrandLanding = ({ page }) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(254,202,202,0.55),_transparent_35%),linear-gradient(180deg,_#fff7ed_0%,_#ffffff_38%,_#fff1f2_100%)]">
      <SeoHead
        title={page.title}
        description={page.description}
        canonicalPath={page.path}
        structuredData={structuredData}
      />
      <Navbar />

      <section className="px-4 pb-12 pt-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-white/90 px-8 py-12 shadow-xl ring-1 ring-red-100 backdrop-blur-md md:px-14 md:py-16">
          <div className="flex h-14 items-center">
            <img
              src={getBrandLogo(page.brand)}
              alt={page.displayBrand}
              width="80"
              height="80"
              className="h-12 w-auto object-contain"
            />
          </div>
          <h1 className="mt-4 max-w-3xl text-4xl font-bold tracking-tight text-gray-900 md:text-6xl">{page.heroTitle}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">{page.heroText}</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <Link
              to={page.marketplacePath}
              className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-7 py-4 font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              {page.primaryCtaLabel}
            </Link>
            <Link
              to="/sell"
              className="inline-flex items-center justify-center rounded-2xl border border-gray-300 px-7 py-4 font-semibold text-gray-700 transition hover:border-red-400 hover:text-red-500"
            >
              {page.secondaryCtaLabel}
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-4 sm:px-6 lg:px-8">
        <TrustBar />
      </section>

      <StepsSection steps={page.steps} />

      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-white p-8 shadow-sm ring-1 ring-gray-100 md:p-12">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-[1.5rem] bg-gradient-to-br from-orange-50 to-white p-7 ring-1 ring-orange-100">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-orange-500">Kopen</p>
              <ul className="mt-5 space-y-3 text-sm leading-7 text-gray-700">
                {page.buyPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-[1.5rem] bg-gradient-to-br from-red-50 to-white p-7 ring-1 ring-red-100">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-red-500">Verkopen</p>
              <ul className="mt-5 space-y-3 text-sm leading-7 text-gray-700">
                {page.sellPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FaqSection faq={page.faq} />

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-gradient-to-r from-red-500 to-orange-500 p-10 text-white shadow-xl md:p-14">
          <h2 className="text-3xl font-bold md:text-4xl">{page.bottomCtaTitle}</h2>
          <p className="mt-4 max-w-2xl text-base leading-7 text-red-50">{page.bottomCtaText}</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <Link
              to={page.marketplacePath}
              className="inline-flex items-center justify-center rounded-2xl bg-white px-7 py-4 font-semibold text-red-500 transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              {page.primaryCtaLabel}
            </Link>
            <Link
              to="/sell"
              className="inline-flex items-center justify-center rounded-2xl border border-white/50 px-7 py-4 font-semibold text-white transition hover:bg-white/10"
            >
              {page.secondaryCtaLabel}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

const GenericLanding = ({ page }) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.heroTitle,
    description: page.description,
    url: `${SITE_URL}${page.path}`,
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(254,202,202,0.35),_transparent_35%),linear-gradient(180deg,_#fff7ed_0%,_#ffffff_34%,_#fff1f2_100%)]">
      <SeoHead
        title={page.title}
        description={page.description}
        canonicalPath={page.path}
        structuredData={structuredData}
      />
      <Navbar />

      <section className="px-4 pb-12 pt-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-white/90 px-8 py-12 shadow-xl ring-1 ring-red-100 backdrop-blur-md md:px-14 md:py-16">
          <h1 className="max-w-3xl text-4xl font-bold tracking-tight text-gray-900 md:text-6xl">{page.heroTitle}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">{page.heroText}</p>
          <Link
            to={page.primaryCta.to}
            className="mt-8 inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-7 py-4 font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            {page.primaryCta.label}
          </Link>
        </div>
      </section>

      <section className="px-4 py-4 sm:px-6 lg:px-8">
        <TrustBar />
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-white p-8 shadow-sm ring-1 ring-gray-100 md:p-12">
          <p className="max-w-3xl text-base leading-8 text-gray-700">{page.introText}</p>

          <div className="mt-10">
            <h2 className="text-3xl font-bold text-gray-900">Populaire merken</h2>
            <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {page.brands.map((brand) => (
                <Link
                  key={brand.label}
                  to={brand.to}
                  className="rounded-[1.5rem] border border-gray-200 bg-gradient-to-br from-orange-50 to-white p-5 transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <h3 className="text-xl font-semibold text-gray-900">{brand.label}</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-600">{brand.text}</p>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-3xl font-bold text-gray-900">
              {page.slug === 'cadeaubonnen-kopen' ? 'Waarom cadeaubonnen kopen via Swapify?' : 'Waarom verkopen via Swapify?'}
            </h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {page.benefits.map((benefit) => (
                <div key={benefit} className="rounded-2xl border border-gray-200 p-5 text-sm font-medium leading-7 text-gray-700">
                  {benefit}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <StepsSection steps={page.steps} />
      <FaqSection faq={page.faq} />

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-gradient-to-r from-red-500 to-orange-500 p-10 text-white shadow-xl md:p-14">
          <h2 className="text-3xl font-bold md:text-4xl">{page.bottomCtaTitle}</h2>
          <p className="mt-4 max-w-2xl text-base leading-7 text-red-50">{page.bottomCtaText}</p>
          <Link
            to={page.bottomCtaTo}
            className="mt-8 inline-flex items-center justify-center rounded-2xl bg-white px-7 py-4 font-semibold text-red-500 transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            {page.bottomCtaLabel}
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

const SeoLandingPage = () => {
  const location = useLocation();
  const { slug } = useParams();

  const genericPage = GENERIC_LANDING_PAGES.find((page) => page.path === location.pathname);
  if (genericPage) {
    return <GenericLanding page={genericPage} />;
  }

  const brandPage = BRAND_LANDING_PAGES.find((page) => page.slug === slug);
  if (!brandPage) {
    return <GenericLanding page={GENERIC_LANDING_PAGES[0]} />;
  }

  return <BrandLanding page={brandPage} />;
};

export default SeoLandingPage;
